/**
 * @description Join jobs to utilization by company plus raw name (LYN-455
 *   Findings). Employees collapse one person across companies only where
 *   a row exists (All Valley, Priority). E-Z and unmatched names stay
 *   company plus name and say so.
 *
 * @dependencies ./assetsCrew
 *
 * @notes jobs.driver often carries a trailing number ("George Jennings 68").
 *   E-Z names have no number and no employees rows. Do not invent a match.
 */

import { parseDriverName } from './assetsCrew';

/**
 * @param {unknown} value
 * @returns {string}
 */
function trimCell(value) {
  return value == null ? '' : String(value).trim();
}

/**
 * @param {object} employee
 * @returns {string|null}
 */
function employeeDriverNumber(employee) {
  const raw = employee?.driverNumber ?? employee?.driver_number;
  const value = trimCell(raw);
  return value || null;
}

/**
 * @param {object} employee
 * @returns {string}
 */
function employeeDisplayName(employee) {
  return [employee?.firstName || employee?.first_name, employee?.lastName || employee?.last_name]
    .map(trimCell)
    .filter(Boolean)
    .join(' ');
}

/**
 * @param {object} employee
 * @param {string} companyName
 * @param {unknown} companyId
 * @returns {boolean}
 */
function employeeCompanyOk(employee, companyName, companyId) {
  const empCompany = trimCell(employee?.companyName || employee?.company_name);
  const empId = employee?.companyId || employee?.company_id;
  if (empId && companyId && String(empId) !== String(companyId)) return false;
  if (empCompany && companyName && empCompany.toLowerCase() !== companyName.toLowerCase()) {
    return false;
  }
  return true;
}

/**
 * Utilization join: company plus the raw driver string.
 *
 * @param {object} input
 * @returns {string}
 */
export function utilizationJoinKey({ companyName, rawName } = {}) {
  return `${trimCell(companyName).toLowerCase()}|${trimCell(rawName).toLowerCase()}`;
}

/**
 * @param {object} job
 * @param {object} row
 * @returns {boolean}
 */
export function jobsMatchUtilization(job, row) {
  return utilizationJoinKey({
    companyName: job?.companyName,
    rawName: job?.driver
  }) === utilizationJoinKey({
    companyName: row?.companyName,
    rawName: row?.name
  });
}

/**
 * Trailing number when present. E-Z names have none.
 *
 * @param {unknown} rawName
 * @returns {string|null}
 */
export function driverJoinKey(rawName) {
  return parseDriverName(rawName)?.driverNumber || null;
}

/**
 * Employees match only where a row exists. Otherwise company plus raw name.
 *
 * @param {object} input
 * @returns {{
 *   key: string,
 *   displayName: string,
 *   driverNumber: string|null,
 *   companyName: string,
 *   via: 'employee'|'companyName'
 * }}
 */
export function resolveDriverIdentity({
  rawName,
  companyName,
  companyId,
  employees
} = {}) {
  const parsed = parseDriverName(rawName);
  const company = trimCell(companyName);
  const name = trimCell(rawName);
  if (Array.isArray(employees) && employees.length > 0) {
    const match = employees.find((row) => {
      if (!employeeCompanyOk(row, company, companyId)) return false;
      const num = employeeDriverNumber(row);
      if (parsed?.driverNumber && num === parsed.driverNumber) return true;
      const empName = employeeDisplayName(row);
      return empName && empName.toLowerCase() === name.toLowerCase();
    });
    if (match) {
      const label = employeeDisplayName(match) || name;
      return {
        key: `employee:${match.id || parsed?.driverNumber || label}`,
        displayName: label,
        driverNumber: parsed?.driverNumber || employeeDriverNumber(match),
        companyName: company,
        via: 'employee'
      };
    }
  }
  return {
    key: `companyName:${companyId || company}:${name}`,
    displayName: company && name ? `${name} · ${company}` : name || company,
    driverNumber: parsed?.driverNumber || null,
    companyName: company,
    via: 'companyName'
  };
}
